import { SyncCoreDataDefinitionsEnumTranslator, TaxonomyFacet, TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';
import { ClientLocaleEntity, ClientTaxonomyEntity, ClientTaxonomyTermEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import React from 'react';
import { VISIBLE_MENTION_FACETS } from '../GovernanceAnalyticsAiAnswers.utils';
import { carriesBrandIdentity } from '../GovernanceTaxonomyManagement.utils';
import { EngineLogo } from './EngineLogo';
import { GovernanceTermUsageCards } from './GovernanceTermUsageCards';
import { localeDisplayName, localeReferenceId } from './localeDisplay.utils';
import { SourceFavicon } from './SourceFavicon';
import { statusLabel, taxonomyTermStatusStyles } from './taxonomyTermStatus.utils';

/**
 * The facets whose tags name something with a presence of its own on the web.
 *
 * The tracked entities the product already reads as things out in the world — a brand, a product, a
 * programme, and each of those on a rival's side — plus a source that gets cited, which is a site by
 * definition. Composed from that one list rather than repeated as a second one: a facet added to the
 * tracked entities names such a thing too, and a copy here would quietly disagree. Every other facet
 * classifies rather than names, and carries no domain for the line to show.
 */
const IDENTITY_FACETS: readonly TaxonomyFacet[] = [...VISIBLE_MENTION_FACETS, TaxonomyFacet.AuthoritySource];

/**
 * The tag's own facet, and the open taxonomy's only where the tag names none.
 *
 * What a tag is used by, what it stands for and which prompt fields can hold it are facts about the
 * tag, so every one of them follows the tag's facet. The open taxonomy stands in for the first one
 * in the list until the route's is resolved, which is one render of a tag read under a facet that
 * is not its own — and on a page opened from another taxonomy's list it would be the wrong facet
 * for good, which is why the tag's own comes first.
 */
export function termOwnFacet(term?: ClientTaxonomyTermEntity, openTaxonomyFacet?: TaxonomyFacet): TaxonomyFacet | undefined {
  return term?.taxonomy?.facet ?? openTaxonomyFacet;
}

export interface IGovernanceTaxonomyTermDetailProps {
  taxonomy: ClientTaxonomyEntity;
  term: ClientTaxonomyTermEntity;
  /** What the usage counts are read through. Without it the page states the tag's own facts alone. */
  api?: ISyncCoreApi;
  /**
   * The project's locales, for naming the one a term was derived from.
   *
   * Only a locale-facet page loads any, which is the only page that shows a source locale.
   */
  locales: ClientLocaleEntity[];
  localesLoading?: boolean;
  /** Opens the face of the page that lists the prompts themselves, where the page offers one. */
  onShowPrompts?: () => void;
  /** What a status change that failed said, so the page does not swallow it. */
  error?: string;
}

/**
 * A taxonomy term's own page: what the term is, before anything about changing it.
 *
 * The page a link lands on is read first. Everything on it is a statement about the term — its
 * name, what it is a term of, its key, its status, what it is for, what else it is called, and when
 * it entered the taxonomy. The ways to change it belong to the page's header strip, which offers
 * each of them exactly once; the card carries none of its own, so no action is worded two ways in
 * one screen. The edit form is one click away at its own URL, so a reference to a term is never an
 * edit command.
 *
 * Facet-dependent fields appear only where the facet carries them: a source locale is a fact about
 * a locale term and meaningless on any other, so it is shown on locale terms and nowhere else. The
 * identity line under the name follows the same rule, on the facets that name something with a site
 * of its own.
 */
export function GovernanceTaxonomyTermDetail({
  taxonomy,
  term,
  api,
  locales,
  localesLoading,
  onShowPrompts,
  error,
}: IGovernanceTaxonomyTermDetailProps) {
  const status = term.status as TaxonomyTermStatus | undefined;
  const styles = taxonomyTermStatusStyles(status);
  const key = String(term.key || '').trim();
  const aliases = (term.aliases || []).filter((alias) => !!alias.trim());
  const isLocaleTerm = taxonomy.facet === TaxonomyFacet.Locale;
  const facet = termOwnFacet(term, taxonomy.facet);

  return (
    <div className="max-w-3xl rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="min-w-0 border-b border-slate-200 px-6 py-4">
        <div className="flex flex-wrap items-center gap-2">
          {/* The mark in front of the name, on the engines this app carries one for. Decoration
              here rather than a label: the name it belongs to is right beside it. */}
          <EngineLogo termKey={key} size="heading" />
          <h3 className="text-lg font-semibold text-slate-900">{term.name}</h3>
          {status ? <span className={`rounded-md border px-2 py-0.5 text-xs ${styles.badge}`}>{statusLabel(status)}</span> : null}
        </div>
        {facet && IDENTITY_FACETS.includes(facet) ? <TermIdentityLine api={api} term={term} /> : null}
      </div>

      <div className="space-y-5 px-6 py-5">
        {/* What uses the tag, before what the tag is: the first question a reader arrives with is
            whether it is carrying any weight, and every number here opens the list it counted. */}
        <GovernanceTermUsageCards api={api} facet={facet} termKey={key} onShowPrompts={onShowPrompts} />

        {error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}

        <dl className="space-y-4">
          <div>
            <dt className="text-sm font-bold text-slate-700">Facet</dt>
            <dd className="mt-1 text-sm text-slate-600">{taxonomy.name}</dd>
          </div>

          {/* Absent rather than empty on a term that has no key: only a WriteAll caller may set one
              today, and a blank row would read as a value the operator failed to fill in. */}
          {key ? (
            <div>
              <dt className="text-sm font-bold text-slate-700">Key</dt>
              <dd className="mt-1 font-mono text-sm text-slate-600">{key}</dd>
            </div>
          ) : null}

          <div>
            <dt className="text-sm font-bold text-slate-700">Description</dt>
            <dd className="mt-1 whitespace-pre-line text-sm text-slate-600">{term.description || 'No description provided.'}</dd>
          </div>

          {aliases.length ? (
            <div>
              <dt className="text-sm font-bold text-slate-700">Also known as</dt>
              <dd className="mt-1 text-sm text-slate-600">{aliases.join(', ')}</dd>
            </div>
          ) : null}

          {/* The brand colour, on the six facets that carry one. A tag with a domain but no colour
              — the common case — reads plainly rather than as a blank swatch. */}
          {carriesBrandIdentity(facet) ? <TermColorRow term={term} /> : null}

          {/* The one facet where a source locale means anything: a locale term IS a locale, and
              every other facet's terms carry no `source.locale` for the field to report. */}
          {isLocaleTerm ? (
            <div>
              <dt className="text-sm font-bold text-slate-700">Source locale</dt>
              <dd className="mt-1 text-sm text-slate-600">{sourceLocaleLabel(term, locales, localesLoading)}</dd>
            </div>
          ) : null}
        </dl>
      </div>

      <div className="border-t border-slate-200 px-6 py-3 text-xs text-slate-500">{auditLine(term)}</div>
    </div>
  );
}

/**
 * What the tag stands for out in the world: the site it is the tag of.
 *
 * Only what is actually stored is shown — a tag without one gets no line at all rather than an
 * empty one, and a domain nobody curated is not guessed at from anywhere else. The favicon is
 * decoration on the domain beside it, so a host with no icon costs the line nothing but a blank box
 * of its own width.
 */
function TermIdentityLine({ api, term }: { api?: ISyncCoreApi; term: ClientTaxonomyTermEntity }) {
  const domain = String(term.thirdParty?.domain || '').trim();

  if (!domain) {
    return null;
  }

  return (
    <div className="mt-1 flex items-center gap-2 text-sm text-slate-500">
      <SourceFavicon api={api} domain={domain} size={16} />
      <span>{domain}</span>
    </div>
  );
}

/**
 * The tag's brand colour: a swatch beside the stored hex, or a plain line when none is set.
 *
 * The hex stays visible as text, so the colour is never carried by the swatch alone — the swatch is
 * decoration on the value beside it. A brand-identity tag with a domain but no colour is the common
 * case and reads as "not set yet" rather than showing an empty swatch.
 */
function TermColorRow({ term }: { term: ClientTaxonomyTermEntity }) {
  const color = String(term.color || '')
    .trim()
    .toLowerCase();

  return (
    <div>
      <dt className="text-sm font-bold text-slate-700">Brand colour</dt>
      <dd className="mt-1 text-sm text-slate-600">
        {color ? (
          <span className="inline-flex items-center gap-2">
            <span aria-hidden="true" className="h-4 w-4 rounded border border-slate-200" style={{ backgroundColor: color }} />
            <span className="font-mono">{color}</span>
          </span>
        ) : (
          'No brand colour set yet.'
        )}
      </dd>
    </div>
  );
}

/**
 * The locale a term was derived from, by name.
 *
 * Never the raw id: an id is not something a reader can act on, and the whole point of naming the
 * locale is that they recognise it. While the locales are still loading the row says so rather than
 * claiming the term has none.
 */
function sourceLocaleLabel(term: ClientTaxonomyTermEntity, locales: ClientLocaleEntity[], localesLoading?: boolean): string {
  const localeId = localeReferenceId(term.source?.locale);
  if (!localeId) {
    return 'Not set';
  }

  const locale = locales.find((entry) => String(entry.id) === localeId);
  if (locale) {
    return localeDisplayName(locale);
  }

  return localesLoading ? 'Loading…' : 'Not set';
}

/** When the term entered the taxonomy, when it last changed, and what put it there. */
function auditLine(term: ClientTaxonomyTermEntity): string {
  const parts = [`Created ${timestampLabel(term.createdAt)}`, `updated ${timestampLabel(term.updatedAt)}`];

  if (term.origin) {
    parts.push(`origin ${SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyTermOrigin', term.origin)}`);
  }

  return `${parts.join(' · ')}.`;
}

function timestampLabel(value: unknown): string {
  const timestamp = toTimestamp(value);
  return timestamp === undefined ? 'unknown' : new Date(timestamp).toLocaleString();
}

/**
 * A timestamp out of a date the client may hand over either way.
 *
 * The runtime entity carries moments and a serialized one carries numbers; `valueOf` is what both
 * answer, so the value is read rather than the type guessed at.
 */
function toTimestamp(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (value && typeof value === 'object') {
    const extracted = (value as { valueOf?: () => unknown }).valueOf?.();
    if (typeof extracted === 'number' && Number.isFinite(extracted)) {
      return extracted;
    }
  }

  return undefined;
}
